import { useCallback } from 'react';
import { keepPreviousData, queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useServerFn } from '@tanstack/react-start';
import chunk from 'lodash/chunk';

import type {
  CheckActiveCodePathParameters,
  GetObjectsWithFindingsPathParameters,
  TLAbapFormatRequest,
  TLAbapFormatResponse,
  TLApplyPseudocodeRequest,
  TLApplyPseudocodeResponse,
  TLObjectIdsRequest,
  TLObjectIdsResponse,
  TLRemedyTrBulkAcceptedResponse,
  TLRemedyTrBulkFromProgramRequest,
  TLSuccessResponse,
  TLUpdateFindingsStatusRequest,
  TLValidateCodeRequest,
  TLValidateCodeResponse,
  UpdateFindingsStatusPathParameters,
} from '../gen';
import {
  applyPseudocode,
  checkActiveCode,
  formatAbapCode,
  getObjectsWithFindings,
  reconcileProgramFileSubobjectInsertParentTrViaAdtAsync,
  updateFindingsStatus,
} from '../gen';

import { apiInstance } from '@/api/api-instance';
import { useCodeRemedyFlags } from '@/components/features/code-remedy/hooks/use-code-remedy-flags';
import { isScanActuallyInProgress } from '@/components/features/code-remedy/utils';
import { useMutationNext } from '@/query';
import type { MutationOptionsNext } from '@/query/types';
import { useServerFnStream } from '@/query/use-server-fn-stream';
import { abapStaticFixList } from '@/server/abap/abap-static-fixes';
import { fixObjectsWithIssuesStream, fixObjectWithIssues } from '@/server/remedy/issue-remediate.function';
import { findObjectsWithIssuesStream } from '@/server/remedy/issue-search.function';
import type {
  TLAIAbapStaticFixListResponse,
  TLAIActivationRequest,
  TLAIActivationResponse,
  TLAIApiError,
  TLAIFindingsForScanObjectRequest,
  TLAIFindingsListResponse,
  TLAIFixLibraryExportFormat,
  TLAIFixLibraryExportSelectionRequest,
  TLAIFixLibraryImportRequest,
  TLAIFixLibraryImportResponse,
  TLAIFixTemplateCreateRequest,
  TLAIFixTemplateCreateResponse,
  TLAIFixTemplateDeleteRequest,
  TLAIFixTemplateDeleteResponse,
  TLAIFixTemplateListResponse,
  TLAIFixTemplateUpdateRequest,
  TLAIFixTemplateUpdateResponse,
  TLAIFleAnalyzeResponse,
  TLAIFleRemediateRequest,
  TLAIIssueFixesListResponse,
  TLAINextFindObjectsWithIssuesRequest,
  TLAINextFixObjectsWithIssuesItem,
  TLAINextFixObjectsWithIssuesRequest,
  TLAINextFixObjectWithIssuesRequest,
  TLAINextFixObjectWithIssuesResponse,
  TLAINextObjectIssue,
  TLAIObjectAssignmentRequest,
  TLAIObjectAssignmentResponse,
  TLAIObjectDetailsRequest,
  TLAIObjectSyntaxCheckRequest,
  TLAIObjectSyntaxCheckResponse,
  TLAIPackageImportCreateRequest,
  TLAIPackageImportPreviewRequest,
  TLAIPackageImportPreviewResponse,
  TLAIRemedy,
  TLAIRemedyCreateRequest,
  TLAIRemedyCreateResponse,
  TLAIRemedyDeleteRequest,
  TLAIRemedyDetailRequest,
  TLAIRemedyDetailResponse,
  TLAIRemedyListResponse,
  TLAIRemedyObjectsListResponse,
  TLAIRemedyRelatedObjectsRequest,
  TLAIRemedyUpdateRequest,
  TLAIRemedyV3CancelScansRequest,
  TLAIRemedyV3CreateResponse,
  TLAIRemedyV3DetailResponse,
  TLAIRemedyV3FindingsListResponse,
  TLAIRemedyV3ListResponse,
  TLAIRemedyV3ModLogRequest,
  TLAIRemedyV3ModLogResponse,
  TLAIRemedyV3ObjectsListResponse,
  TLAIRemedyV3ScanRequest,
  TLAIRemedyV3SyntaxCheckRequest,
  TLAIRemedyV3SyntaxCheckResponse,
  TLAIRemedyV3UpdateFindingStatusRequest,
  TLAIRemedyV3UpdateFindingStatusResponse,
  TLAIRemedyV3UpdateObjectStatusRequest,
  TLAIRemedyV3UpdateObjectStatusResponse,
  TLAIRescanObjectRequest,
  TLAIRescanResponse,
  TLAIResolveIssuesRequest,
  TLAIResolveIssuesResponse,
  TLAIRulesListResponse,
  TLAISapObjectImportCreateRequest,
  TLAISapObjectSearchRequest,
  TLAISapObjectSearchResponse,
  TLAISapObjectSearchResult,
  TLAISapUpdateRequest,
  TLAISapUpdateResponse,
  TLAIScanResponse,
  TLAISourceCodeRequest,
  TLAISourceCodeResponse,
  TLAIStartRemedyRequest,
  TLAIStartRemedyResponse,
  TLAITransportImportCreateRequest,
  TLAITransportImportPreviewRequest,
  TLAITransportImportPreviewResponse,
  TLAIUpdateSourceCodeRequest,
  TLAIUpdateSourceCodeResponse,
} from '@/types/api';
import { getApiClient } from '@/utils/api-client';
import type { QueryOptions } from '@/utils/types';
import { createUrlPathWithParams } from '@/utils/url';

export const fixTemplatesQueryOptions = (options?: QueryOptions<TLAIFixTemplateListResponse>) =>
  queryOptions({
    queryKey: ['fix-templates'],
    queryFn: async () => (await getApiClient()).get<TLAIFixTemplateListResponse>('/code-remedy/fix-templates'),
    ...options,
  });

export function useFixTemplatesQuery(options?: QueryOptions<TLAIFixTemplateListResponse>) {
  return useQuery(fixTemplatesQueryOptions(options));
}

export function useFixTemplateCreateMutation(
  options?: MutationOptionsNext<TLAIFixTemplateCreateResponse, TLAIApiError, TLAIFixTemplateCreateRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLAIFixTemplateCreateRequest) =>
      (await getApiClient()).post<TLAIFixTemplateCreateResponse>('/code-remedy/fix-template', input),
    invalidateQueries: [fixTemplatesQueryOptions().queryKey],
  });
}

export function useFixTemplateUpdateMutation(
  options?: MutationOptionsNext<TLAIFixTemplateUpdateResponse, TLAIApiError, TLAIFixTemplateUpdateRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ fix_template_id, ...input }: TLAIFixTemplateUpdateRequest) =>
      (await getApiClient()).put<TLAIFixTemplateUpdateResponse>(
        createUrlPathWithParams('/code-remedy/fix-templates', { path: [fix_template_id] }),
        input,
      ),
    invalidateQueries: [fixTemplatesQueryOptions().queryKey],
  });
}

export function useFixTemplateDeleteMutation(
  options?: MutationOptionsNext<TLAIFixTemplateDeleteResponse, TLAIApiError, TLAIFixTemplateDeleteRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ fix_template_id }: TLAIFixTemplateDeleteRequest) =>
      (await getApiClient()).delete<TLAIFixTemplateDeleteResponse>(
        createUrlPathWithParams('/code-remedy/fix-templates', { path: [fix_template_id] }),
      ),
    invalidateQueries: [fixTemplatesQueryOptions().queryKey],
  });
}

/**
 * Download the tenant's fix library as a file.
 *
 * Returns the body only — the axios client does not surface response headers — so the caller
 * names the file rather than reading Content-Disposition.
 */
export async function exportFixTemplates(format: TLAIFixLibraryExportFormat): Promise<Blob> {
  return apiInstance<Blob>({
    url: '/code-remedy/fix-templates/export',
    method: 'GET',
    params: { format },
    responseType: 'blob',
  });
}

/**
 * Export only the given rules. A POST rather than a GET with ids in the query string: at the
 * library cap of 1000 rules the ids run to tens of kilobytes and exceed the server's
 * request-line limit. The server refuses the whole selection if any rule cannot be read, so a
 * stale id surfaces as an error rather than a silently smaller file.
 */
export async function exportSelectedFixTemplates(
  format: TLAIFixLibraryExportFormat,
  fixTemplateIds: string[],
): Promise<Blob> {
  return apiInstance<Blob>({
    url: '/code-remedy/fix-templates/export',
    method: 'POST',
    params: { format },
    data: { fix_template_ids: fixTemplateIds } satisfies TLAIFixLibraryExportSelectionRequest,
    responseType: 'blob',
  });
}

/**
 * Load rules from a JSONL export into the tenant's library. Rules are matched by title and
 * `on_conflict` decides what a match does; the response reports every row.
 */
export function useFixTemplateImportMutation(
  options?: MutationOptionsNext<TLAIFixLibraryImportResponse, TLAIApiError, TLAIFixLibraryImportRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ file, on_conflict }: TLAIFixLibraryImportRequest) => {
      const formData = new FormData();
      formData.append('file', file);
      return (await getApiClient()).post<TLAIFixLibraryImportResponse>(
        createUrlPathWithParams('/code-remedy/fix-templates/import', { query: { on_conflict } }),
        formData,
      );
    },
    invalidateQueries: [fixTemplatesQueryOptions().queryKey],
  });
}

export const remediesQueryOptions = (options?: QueryOptions<TLAIRemedyListResponse>) =>
  queryOptions({
    queryKey: ['remedies'],
    queryFn: async () => (await getApiClient()).get<TLAIRemedyListResponse>('/code-remedy/remedies'),
    ...options,
  });

export function useRemediesQuery(options?: QueryOptions<TLAIRemedyListResponse>) {
  return useQuery(remediesQueryOptions(options));
}

export const remedyDetailQueryOptions = (
  { remedy_id }: TLAIRemedyDetailRequest,
  options?: QueryOptions<TLAIRemedyDetailResponse>,
) =>
  queryOptions({
    queryKey: ['remedies', remedy_id],
    queryFn: async () =>
      (await getApiClient()).get<TLAIRemedyDetailResponse>(
        createUrlPathWithParams('/code-remedy/remedies', { path: [remedy_id] }),
      ),
    enabled: Boolean(remedy_id),
    ...options,
  });

export function useRemedyDetailQuery(input: TLAIRemedyDetailRequest, options?: QueryOptions<TLAIRemedyDetailResponse>) {
  return useQuery(remedyDetailQueryOptions(input, options));
}

export const remedyRelatedObjectsQueryOptions = (
  { remedy_id, object_id }: TLAIRemedyRelatedObjectsRequest,
  options?: QueryOptions<TLAIRemedyObjectsListResponse>,
) =>
  queryOptions({
    ...options,
    queryKey: ['remedies', remedy_id, 'objects', object_id],
    queryFn: async () =>
      (await getApiClient()).get<TLAIRemedyObjectsListResponse>(
        createUrlPathWithParams('/code-remedy/remedies', {
          path: [remedy_id, 'objects', object_id],
        }),
      ),
    enabled: Boolean(remedy_id && object_id),
    refetchInterval: ({ state }) => {
      if (isScanActuallyInProgress(object_id, state.data?.scans)) {
        return 2000;
      }
      return false;
    },
  });

export function useRemedyRelatedObjectsQuery(
  input: TLAIRemedyRelatedObjectsRequest,
  options?: QueryOptions<TLAIRemedyObjectsListResponse>,
) {
  return useQuery(remedyRelatedObjectsQueryOptions(input, options));
}

export const findingsForScanObjectQueryOptions = (
  { scan_id, object_id }: TLAIFindingsForScanObjectRequest,
  options?: QueryOptions<TLAIFindingsListResponse>,
) =>
  queryOptions({
    queryKey: ['findings', scan_id, object_id],
    queryFn: async () =>
      (await getApiClient()).get<TLAIFindingsListResponse>(
        createUrlPathWithParams('/code-remedy/scans', {
          path: [scan_id, 'objects', object_id, 'findings'],
        }),
      ),
    enabled: Boolean(scan_id && object_id),
    ...options,
  });

export function useFindingsForScanObjectQuery(
  input: TLAIFindingsForScanObjectRequest,
  options?: QueryOptions<TLAIFindingsListResponse>,
) {
  return useQuery(findingsForScanObjectQueryOptions(input, options));
}

export const fixesForScanObjectQueryOptions = (
  { scan_id, object_id }: TLAIFindingsForScanObjectRequest,
  options?: QueryOptions<TLAIIssueFixesListResponse>,
) =>
  queryOptions({
    queryKey: ['fixes', scan_id, object_id],
    queryFn: async () =>
      (await getApiClient()).get<TLAIIssueFixesListResponse>(
        createUrlPathWithParams('/code-remedy/scans', {
          path: [scan_id, 'objects', object_id, 'fixes'],
        }),
      ),
    enabled: Boolean(scan_id && object_id),
    ...options,
  });

export function useFixesForScanObjectQuery(
  input: TLAIFindingsForScanObjectRequest,
  options?: QueryOptions<TLAIIssueFixesListResponse>,
) {
  return useQuery(fixesForScanObjectQueryOptions(input, options));
}

export function useRemedyCreateMutation(
  options?: MutationOptionsNext<TLAIRemedyCreateResponse, TLAIApiError, TLAIRemedyCreateRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLAIRemedyCreateRequest) =>
      (await getApiClient()).post<TLAIRemedyCreateResponse>('/code-remedy/create', input),
    invalidateQueries: [remediesQueryOptions().queryKey],
  });
}

export function useResolveIssuesMutation(
  options?: MutationOptionsNext<TLAIResolveIssuesResponse, TLAIApiError, TLAIResolveIssuesRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLAIResolveIssuesRequest) =>
      (await getApiClient()).post<TLAIResolveIssuesResponse>(
        createUrlPathWithParams('/code-remedy/objects', { path: [input.object_id, 'resolve'] }),
        { issues: input.issues },
      ),
    invalidateQueries: (_, variables) => [
      ['findings', variables.object_id],
      ['fixes', variables.object_id],
    ],
  });
}

export function useRescanObjectMutation(
  options?: MutationOptionsNext<TLAIRescanResponse, TLAIApiError, TLAIRescanObjectRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLAIRescanObjectRequest) =>
      (await getApiClient()).post<TLAIRescanResponse>(
        createUrlPathWithParams('/code-remedy/remedies', {
          path: [input.remedy_id, 'objects', input.parent_id, 'rescan'],
        }),
      ),
    invalidateQueries: (_, variables) => [
      ['remedies', variables.remedy_id],
      ['code-remediation-v3', 'remedies', variables.remedy_id],
    ],
  });
}

export function useSaveToSapMutation(
  options?: MutationOptionsNext<TLAISapUpdateResponse, TLAIApiError, TLAISapUpdateRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLAISapUpdateRequest) =>
      (await getApiClient()).post<TLAISapUpdateResponse>('/code-remedy/remedies/objects/save-to-sap', input),
    invalidateQueries: (_, variables) => [['remedies', variables.remedy_id], remediesQueryOptions().queryKey],
  });
}

export function useObjectSyntaxCheckMutation(
  options?: MutationOptionsNext<TLAIObjectSyntaxCheckResponse, TLAIApiError, TLAIObjectSyntaxCheckRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLAIObjectSyntaxCheckRequest) =>
      (await getApiClient()).post<TLAIObjectSyntaxCheckResponse>(
        createUrlPathWithParams('/code-remedy/syntax-check'),
        input,
      ),
  });
}

export function useStartRemedyMutation(
  options?: MutationOptionsNext<TLAIStartRemedyResponse, TLAIApiError, TLAIStartRemedyRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLAIStartRemedyRequest) =>
      (await getApiClient()).post<TLAIStartRemedyResponse>('/code-remedy/start', input),
    invalidateQueries: [remediesQueryOptions().queryKey],
  });
}

export function useAssignObjectsToUsersMutation(
  options?: MutationOptionsNext<TLAIObjectAssignmentResponse, TLAIApiError, TLAIObjectAssignmentRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ remedy_id, ...input }: TLAIObjectAssignmentRequest) =>
      (await getApiClient()).patch<TLAIObjectAssignmentResponse>(
        createUrlPathWithParams('/code-remedy/remedies', { path: [remedy_id] }),
        input,
      ),
    invalidateQueries: (_, { remedy_id }) => [
      remediesQueryOptions().queryKey,
      remedyDetailQueryOptions({ remedy_id }).queryKey,
    ],
  });
}

export const remediesV3QueryOptions = (options?: QueryOptions<TLAIRemedyV3ListResponse>) =>
  queryOptions({
    queryKey: ['remedies-v3'],
    queryFn: async () => (await getApiClient()).get<TLAIRemedyV3ListResponse>('/code-remediation-v3/remedies'),
    ...options,
  });

export function useRemediesV3Query(options?: QueryOptions<TLAIRemedyV3ListResponse>) {
  return useQuery(remediesV3QueryOptions(options));
}

export const remedyV3DetailQueryOptions = (
  { remedy_id }: TLAIRemedyDetailRequest,
  options?: QueryOptions<TLAIRemedyV3DetailResponse>,
) =>
  queryOptions({
    queryKey: ['remedies-v3', remedy_id],
    queryFn: async () =>
      (await getApiClient()).get<TLAIRemedyV3DetailResponse>(
        createUrlPathWithParams('/code-remediation-v3/remedies', { path: [remedy_id] }),
      ),
    enabled: Boolean(remedy_id),
    ...options,
  });

export function useRemedyV3DetailQuery(
  input: TLAIRemedyDetailRequest,
  options?: QueryOptions<TLAIRemedyV3DetailResponse>,
) {
  return useQuery(remedyV3DetailQueryOptions(input, options));
}

export const findingsV3QueryOptions = (
  { scan_id, object_id }: TLAIFindingsForScanObjectRequest,
  options?: QueryOptions<TLAIRemedyV3FindingsListResponse>,
) =>
  queryOptions({
    queryKey: ['findings-v3', scan_id, object_id],
    queryFn: async () =>
      (await getApiClient()).get<TLAIRemedyV3FindingsListResponse>(
        createUrlPathWithParams('/code-remediation-v3/objects', {
          path: [object_id, 'scans', scan_id, 'findings'],
        }),
      ),
    enabled: Boolean(scan_id && object_id),
    ...options,
  });

export function useFindingsV3Query(
  input: TLAIFindingsForScanObjectRequest,
  options?: QueryOptions<TLAIRemedyV3FindingsListResponse>,
) {
  return useQuery(findingsV3QueryOptions(input, options));
}

export function useRemedyV3CreateMutation(
  options?: MutationOptionsNext<TLAIRemedyV3CreateResponse, TLAIApiError, TLAIObjectDetailsRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLAIObjectDetailsRequest) =>
      (await getApiClient()).post<TLAIRemedyV3CreateResponse>('/code-remediation-v3/start', input),
    invalidateQueries: [remediesV3QueryOptions().queryKey],
  });
}

export function useRemedyRenameMutation(
  options?: MutationOptionsNext<TLAIRemedy, TLAIApiError, TLAIRemedyUpdateRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ remedy_id, name }: TLAIRemedyUpdateRequest) =>
      (await getApiClient()).patch<TLAIRemedy>(
        createUrlPathWithParams('/code-remediation-v3/remedies', { path: [remedy_id, 'details'] }),
        { name },
      ),
    invalidateQueries: [remediesV3QueryOptions().queryKey],
  });
}

export function useRemedyDeleteMutation(options?: MutationOptionsNext<void, TLAIApiError, TLAIRemedyDeleteRequest>) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ remedy_id }: TLAIRemedyDeleteRequest) =>
      (await getApiClient()).delete<void>(
        createUrlPathWithParams('/code-remediation-v3/remedies', { path: [remedy_id] }),
      ),
    invalidateQueries: [remediesV3QueryOptions().queryKey],
  });
}

export type TLAIPackageImportPreviewParams = TLAIPackageImportPreviewRequest & {
  limit: number;
  offset: number;
};

export const packageImportPreviewQueryOptions = (
  { package_name, package_name_end, sap_version, limit, offset }: TLAIPackageImportPreviewParams,
  options?: QueryOptions<TLAIPackageImportPreviewResponse>,
) =>
  queryOptions({
    queryKey: ['package-import-preview', package_name, package_name_end, sap_version, limit, offset],
    queryFn: async () =>
      (await getApiClient()).post<TLAIPackageImportPreviewResponse>(
        createUrlPathWithParams('/code-remediation-v3/package-imports/preview', { query: { limit, offset } }),
        { package_name, package_name_end, sap_version },
      ),
    enabled: Boolean(package_name),
    placeholderData: keepPreviousData,
    ...options,
  });

export function usePackageImportPreviewQuery(
  input: TLAIPackageImportPreviewParams,
  options?: QueryOptions<TLAIPackageImportPreviewResponse>,
) {
  return useQuery(packageImportPreviewQueryOptions(input, options));
}

export function usePackageImportCreateMutation(
  options?: MutationOptionsNext<TLAIRemedyV3CreateResponse, TLAIApiError, TLAIPackageImportCreateRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLAIPackageImportCreateRequest) =>
      (await getApiClient()).post<TLAIRemedyV3CreateResponse>('/code-remediation-v3/package-imports', input),
    invalidateQueries: [remediesV3QueryOptions().queryKey],
  });
}

export type TLAITransportImportPreviewParams = TLAITransportImportPreviewRequest & {
  limit: number;
  offset: number;
};

export const transportImportPreviewQueryOptions = (
  { transport_numbers, sap_version, limit, offset }: TLAITransportImportPreviewParams,
  options?: QueryOptions<TLAITransportImportPreviewResponse>,
) =>
  queryOptions({
    queryKey: ['transport-import-preview', transport_numbers.join(','), sap_version, limit, offset],
    queryFn: async () =>
      (await getApiClient()).post<TLAITransportImportPreviewResponse>(
        createUrlPathWithParams('/code-remediation-v3/transport-imports/preview', { query: { limit, offset } }),
        { transport_numbers, sap_version },
      ),
    enabled: transport_numbers.length > 0,
    placeholderData: keepPreviousData,
    ...options,
  });

export function useTransportImportPreviewQuery(
  input: TLAITransportImportPreviewParams,
  options?: QueryOptions<TLAITransportImportPreviewResponse>,
) {
  return useQuery(transportImportPreviewQueryOptions(input, options));
}

export function useTransportImportCreateMutation(
  options?: MutationOptionsNext<TLAIRemedyV3CreateResponse, TLAIApiError, TLAITransportImportCreateRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLAITransportImportCreateRequest) =>
      (await getApiClient()).post<TLAIRemedyV3CreateResponse>('/code-remediation-v3/transport-imports', input),
    invalidateQueries: [remediesV3QueryOptions().queryKey],
  });
}

export type TLAISapObjectSearchParams = TLAISapObjectSearchRequest & {
  limit: number;
  offset: number;
};

/** The backend caps `limit` at 100 per page (`GET /code-remediation-v3/sap-objects`). */
export const SAP_OBJECT_SEARCH_MAX_PAGE_LIMIT = 100;

const sapObjectSearchPath = ({
  object_type,
  name_pattern,
  name_end,
  sap_version,
  limit,
  offset,
}: TLAISapObjectSearchParams) =>
  createUrlPathWithParams('/code-remediation-v3/sap-objects', {
    query: { object_type, name_pattern, name_end, sap_version, limit, offset },
  });

export const sapObjectSearchQueryOptions = (
  { object_type, name_pattern, name_end, sap_version, limit, offset }: TLAISapObjectSearchParams,
  options?: QueryOptions<TLAISapObjectSearchResponse>,
) =>
  queryOptions({
    queryKey: ['sap-object-search', object_type, name_pattern, name_end, sap_version, limit, offset],
    queryFn: async () =>
      (await getApiClient()).get<TLAISapObjectSearchResponse>(
        sapObjectSearchPath({ object_type, name_pattern, name_end, sap_version, limit, offset }),
      ),
    enabled: Boolean(name_pattern),
    placeholderData: keepPreviousData,
    ...options,
  });

export function useSapObjectSearchQuery(
  input: TLAISapObjectSearchParams,
  options?: QueryOptions<TLAISapObjectSearchResponse>,
) {
  return useQuery(sapObjectSearchQueryOptions(input, options));
}

/**
 * Fetch every object matching a search, across all pages.
 *
 * The preview tracks *deselected* rows, but the object-import endpoint takes a *selected* list and
 * rows on unvisited pages are implicitly selected — so the caller needs the full match set. The
 * backend caps a search at `ATC_OBJECT_SEARCH_MAX_RESULTS` (500 by default), which bounds this to a
 * handful of requests. Reads go direct rather than through the query cache so the import acts on
 * what SAP returns now, not a stale preview page.
 */
export async function fetchAllSapObjectSearchResults(
  request: TLAISapObjectSearchRequest,
): Promise<TLAISapObjectSearchResult[]> {
  const client = await getApiClient();
  const limit = SAP_OBJECT_SEARCH_MAX_PAGE_LIMIT;
  const firstPage = await client.get<TLAISapObjectSearchResponse>(
    sapObjectSearchPath({ ...request, limit, offset: 0 }),
  );

  const remainingOffsets: number[] = [];
  for (let offset = limit; offset < firstPage.total_count; offset += limit) {
    remainingOffsets.push(offset);
  }

  const remainingPages = await Promise.all(
    remainingOffsets.map((offset) =>
      client.get<TLAISapObjectSearchResponse>(sapObjectSearchPath({ ...request, limit, offset })),
    ),
  );

  return [firstPage, ...remainingPages].flatMap((page) => page.objects);
}

export function useSapObjectImportCreateMutation(
  options?: MutationOptionsNext<TLAIRemedyV3CreateResponse, TLAIApiError, TLAISapObjectImportCreateRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLAISapObjectImportCreateRequest) =>
      (await getApiClient()).post<TLAIRemedyV3CreateResponse>('/code-remediation-v3/sap-object-imports', input),
    invalidateQueries: [remediesV3QueryOptions().queryKey],
  });
}

export function useRemedyV3ScanMutation(
  options?: MutationOptionsNext<TLAIScanResponse, TLAIApiError, TLAIRemedyV3ScanRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ remedy_id, object_ids }: TLAIRemedyV3ScanRequest) =>
      (await getApiClient()).post<TLAIScanResponse>(
        createUrlPathWithParams('/code-remediation-v3/remedies', { path: [remedy_id, 'scan'] }),
        { object_ids },
      ),
    invalidateQueries: (_, { remedy_id }) => [
      remedyV3DetailQueryOptions({ remedy_id }).queryKey,
      remediesV3QueryOptions().queryKey,
    ],
  });
}

export function useRemedyV3CancelScansMutation(
  options?: MutationOptionsNext<TLSuccessResponse, TLAIApiError, TLAIRemedyV3CancelScansRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ scan_ids }: TLAIRemedyV3CancelScansRequest) =>
      (await getApiClient()).patch<TLSuccessResponse>('/code-remediation-v3/scans/cancel', { scan_ids }),
  });
}

export function useRemedyV3UpdateSourceCodeMutation(
  options?: MutationOptionsNext<TLAIUpdateSourceCodeResponse, TLAIApiError, TLAIUpdateSourceCodeRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ remedy_id, ...input }: TLAIUpdateSourceCodeRequest) =>
      (await getApiClient()).post<TLAIUpdateSourceCodeResponse>(
        createUrlPathWithParams('/code-remediation-v3/remedies', { path: [remedy_id, 'update-source-code'] }),
        input,
      ),
    invalidateQueries: (_, { remedy_id, object_id }) => [
      remedyV3DetailQueryOptions({ remedy_id }).queryKey,
      remediesV3QueryOptions().queryKey,
      sourceCodeV3QueryOptions({ remedy_id, object_id }).queryKey,
    ],
  });
}

export const SOURCE_CODE_V3_QUERY_KEY = 'source-code-v3';

export const sourceCodeV3QueryOptions = (
  { remedy_id, object_id, version }: TLAISourceCodeRequest,
  options?: QueryOptions<TLAISourceCodeResponse>,
) =>
  queryOptions({
    // version is appended only when set so pre-existing cache keys and the
    // remedy-wide prefix invalidations keep matching unchanged callers.
    queryKey: [SOURCE_CODE_V3_QUERY_KEY, remedy_id, object_id, ...(version ? [version] : [])],
    queryFn: async () =>
      (await getApiClient()).get<TLAISourceCodeResponse>(
        createUrlPathWithParams('/code-remediation-v3/remedies', {
          path: [remedy_id, 'get-source-code'],
          query: { object_id, ...(version ? { version } : {}) },
        }),
      ),
    enabled: Boolean(remedy_id && object_id),
    ...options,
  });

export function useSourceCodeV3Query(input: TLAISourceCodeRequest, options?: QueryOptions<TLAISourceCodeResponse>) {
  return useQuery(sourceCodeV3QueryOptions(input, options));
}

export function useAssignObjectsToUsersV3Mutation(
  options?: MutationOptionsNext<TLAIObjectAssignmentResponse, TLAIApiError, TLAIObjectAssignmentRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ remedy_id, ...input }: TLAIObjectAssignmentRequest) =>
      (await getApiClient()).patch<TLAIObjectAssignmentResponse>(
        createUrlPathWithParams('/code-remediation-v3/remedies', { path: [remedy_id] }),
        input,
      ),
    invalidateQueries: (_, { remedy_id }) => [
      remediesV3QueryOptions().queryKey,
      remedyV3DetailQueryOptions({ remedy_id }).queryKey,
    ],
  });
}

export const remedyV3RelatedObjectsQueryOptions = (
  { remedy_id, object_id }: TLAIRemedyRelatedObjectsRequest,
  options?: QueryOptions<TLAIRemedyV3ObjectsListResponse>,
) =>
  queryOptions({
    queryKey: ['remedies-v3', remedy_id, 'objects', object_id],
    queryFn: async () =>
      (await getApiClient()).get<TLAIRemedyV3ObjectsListResponse>(
        createUrlPathWithParams('/code-remediation-v3/remedies', {
          path: [remedy_id, object_id],
        }),
      ),
    enabled: Boolean(remedy_id && object_id),
    refetchInterval: ({ state }) => {
      if (isScanActuallyInProgress(object_id, state.data?.scans)) {
        return 2000;
      }
      return false;
    },
    ...options,
  });

export function useRemedyV3RelatedObjectsQuery(
  input: TLAIRemedyRelatedObjectsRequest,
  options?: QueryOptions<TLAIRemedyV3ObjectsListResponse>,
) {
  return useQuery(remedyV3RelatedObjectsQueryOptions(input, options));
}

export function useRemedyV3ActivateMutation(
  options?: MutationOptionsNext<TLAIActivationResponse, TLAIApiError, TLAIActivationRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ remedy_id, ...input }: TLAIActivationRequest) =>
      (await getApiClient()).post<TLAIActivationResponse>(
        createUrlPathWithParams('/code-remediation-v3/remedies', { path: [remedy_id, 'activate'] }),
        input,
      ),
    invalidateQueries: (_, { remedy_id }) => [
      remedyV3DetailQueryOptions({ remedy_id }).queryKey,
      remediesV3QueryOptions().queryKey,
      // Activation changes the ACTIVE version on SAP; drop the cached snapshot
      // so the editor's activated-state indicator recomputes against fresh source.
      [SOURCE_CODE_V3_QUERY_KEY, remedy_id],
    ],
  });
}

export function useRemedyV3SyntaxCheckMutation(
  options?: MutationOptionsNext<TLAIRemedyV3SyntaxCheckResponse, TLAIApiError, TLAIRemedyV3SyntaxCheckRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ remedy_id, ...input }: TLAIRemedyV3SyntaxCheckRequest) =>
      (await getApiClient()).post<TLAIRemedyV3SyntaxCheckResponse>(
        createUrlPathWithParams('/code-remediation-v3/remedies', { path: [remedy_id, 'syntax-check'] }),
        input,
      ),
  });
}

export function useRemedyV3UpdateObjectStatusMutation(
  options?: MutationOptionsNext<
    TLAIRemedyV3UpdateObjectStatusResponse,
    TLAIApiError,
    TLAIRemedyV3UpdateObjectStatusRequest
  >,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ remedy_id, object_id, status }: TLAIRemedyV3UpdateObjectStatusRequest) =>
      (await getApiClient()).patch<TLAIRemedyV3UpdateObjectStatusResponse>(
        createUrlPathWithParams('/code-remediation-v3/remedies', {
          path: [remedy_id, object_id, 'status'],
          query: { status },
        }),
      ),
    invalidateQueries: (_, { remedy_id, object_id }) => [
      remedyV3DetailQueryOptions({ remedy_id }).queryKey,
      remedyV3RelatedObjectsQueryOptions({ remedy_id, object_id }).queryKey,
      remediesV3QueryOptions().queryKey,
    ],
  });
}

export function useRemedyV3ModLogMutation(
  options?: MutationOptionsNext<TLAIRemedyV3ModLogResponse, TLAIApiError, TLAIRemedyV3ModLogRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ remedy_id, ...input }: TLAIRemedyV3ModLogRequest) =>
      (await getApiClient()).post<TLAIRemedyV3ModLogResponse>(
        createUrlPathWithParams('/code-remediation-v3/remedies', { path: [remedy_id, 'mod-log'] }),
        input,
      ),
  });
}

export function useRemedyV3UpdateFindingStatusMutation(
  options?: MutationOptionsNext<
    TLAIRemedyV3UpdateFindingStatusResponse,
    TLAIApiError,
    TLAIRemedyV3UpdateFindingStatusRequest
  >,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ object_id, scan_id, finding_id, status }: TLAIRemedyV3UpdateFindingStatusRequest) =>
      (await getApiClient()).patch<TLAIRemedyV3UpdateFindingStatusResponse>(
        createUrlPathWithParams('/code-remediation-v3/objects', {
          path: [object_id, 'scans', scan_id, 'findings', finding_id, 'status'],
          query: { status },
        }),
      ),
    invalidateQueries: (_, { object_id, scan_id }) => [findingsV3QueryOptions({ object_id, scan_id }).queryKey],
  });
}

export const fleRulesV3QueryOptions = (options?: QueryOptions<TLAIRulesListResponse>) =>
  queryOptions({
    queryKey: ['fle-rules-v3'],
    queryFn: async () => (await getApiClient()).get<TLAIRulesListResponse>('/code-remediation-v3/fle-rules'),
    ...options,
  });

export function useFleRulesV3Query(options?: QueryOptions<TLAIRulesListResponse>) {
  const { enableFleRemediation } = useCodeRemedyFlags();
  return useQuery(fleRulesV3QueryOptions({ enabled: enableFleRemediation, ...options }));
}

export function useFleRemediateV3Mutation(
  options?: MutationOptionsNext<TLAIFleAnalyzeResponse, TLAIApiError, TLAIFleRemediateRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ remedy_id, ...input }) =>
      (await getApiClient()).post<TLAIFleAnalyzeResponse>(
        createUrlPathWithParams('/code-remediation-v3/remedies', { path: [remedy_id, 'fle-remediate'] }),
        input,
      ),
  });
}

export const abapStaticFixListQueryOptions = (options?: QueryOptions<TLAIAbapStaticFixListResponse>) => {
  return queryOptions({
    queryKey: ['abap-static-fix-list'],
    queryFn: () => abapStaticFixList(),
    ...options,
  });
};

export function useAbapStaticFixListQuery(options?: QueryOptions<TLAIAbapStaticFixListResponse>) {
  return useQuery(abapStaticFixListQueryOptions(options));
}

export function useFindObjectsWithIssuesStream() {
  const { maxObjectIdsPerRequest } = useCodeRemedyFlags();

  const findObjectsWithIssuesStreamServerFn = useServerFn(findObjectsWithIssuesStream);

  const [initStream, streamState] = useServerFnStream<TLAINextFindObjectsWithIssuesRequest, TLAINextObjectIssue[]>(
    findObjectsWithIssuesStreamServerFn,
  );

  const start = useCallback(
    (request: TLAINextFindObjectsWithIssuesRequest) =>
      initStream({
        ...request,
        max_object_ids_per_request: request.max_object_ids_per_request ?? maxObjectIdsPerRequest,
      }),
    [maxObjectIdsPerRequest, initStream],
  );

  return [start, streamState] as const;
}

export function useFixObjectsWithIssuesStream() {
  const queryClient = useQueryClient();
  const { maxObjectIdsPerRequest } = useCodeRemedyFlags();
  const { skipCommentingRules, enableFleRemediation } = useCodeRemedyFlags();

  const fixObjectsWithIssuesStreamServerFn = useServerFn(fixObjectsWithIssuesStream);

  const [startStream, streamState] = useServerFnStream<
    TLAINextFixObjectsWithIssuesRequest,
    TLAINextFixObjectsWithIssuesItem[]
  >(fixObjectsWithIssuesStreamServerFn, {
    onReceive: async (items, _items, { remedy_id }) => {
      if (items.length === 0 || !items.some(({ success }) => success)) return;

      await Promise.all(
        items.map(({ object_id }) => [
          queryClient.invalidateQueries({
            queryKey: remedyV3RelatedObjectsQueryOptions({ remedy_id, object_id }).queryKey,
          }),
          queryClient.invalidateQueries({
            queryKey: sourceCodeV3QueryOptions({ remedy_id, object_id }).queryKey,
          }),
        ]),
      );
    },
    onComplete: async (streamedItems, request) => {
      if (streamedItems.length === 0) return;

      const successObjectIds = streamedItems
        .flat()
        .filter(({ success }) => success)
        .map(({ object_id }) => object_id);

      if (successObjectIds.length === 0) return;

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: remediesV3QueryOptions().queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: remedyV3DetailQueryOptions({ remedy_id: request.remedy_id }).queryKey,
        }),
      ]);
    },
  });

  const start = useCallback(
    (request: TLAINextFixObjectsWithIssuesRequest) =>
      startStream({
        ...request,
        skip_fle_remediation: !enableFleRemediation || request.skip_fle_remediation,
        max_object_ids_per_request: request.max_object_ids_per_request ?? maxObjectIdsPerRequest,
        skipCommentingRules,
      }),
    [startStream, enableFleRemediation, maxObjectIdsPerRequest, skipCommentingRules],
  );

  return [start, streamState] as const;
}

export function useFixObjectWithIssuesMutation(
  options?: MutationOptionsNext<TLAINextFixObjectWithIssuesResponse, TLAIApiError, TLAINextFixObjectWithIssuesRequest>,
) {
  const { enableFleRemediation, skipCommentingRules } = useCodeRemedyFlags();

  const fixObjectWithIssuesServerFn = useServerFn(fixObjectWithIssues);
  return useMutationNext({
    ...options,
    mutationFn: ({ remedy_id, object_id, code, issue_keys }: TLAINextFixObjectWithIssuesRequest) =>
      fixObjectWithIssuesServerFn({
        data: {
          remedy_id,
          object_id,
          code,
          issue_keys,
          skip_fle_remediation: !enableFleRemediation,
          skipCommentingRules,
        },
      }),
  });
}

export const getObjectsWithFindingsQueryOptions = (
  { remedyId }: GetObjectsWithFindingsPathParameters,
  {
    object_ids,
    max_findings_object_ids_per_request,
    ...rest
  }: TLObjectIdsRequest & { max_findings_object_ids_per_request: number },
  options?: QueryOptions<TLObjectIdsResponse>,
) => {
  const objectChunks = max_findings_object_ids_per_request
    ? chunk(object_ids, max_findings_object_ids_per_request)
    : [object_ids];

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return queryOptions({
    queryKey: ['code-remediation-v3', 'remedies', remedyId, 'objects-with-findings', { object_ids, ...rest }],
    queryFn: async ({ signal }) => {
      const computedObjectIds: string[] = [];

      for (const objects of objectChunks) {
        const response = await getObjectsWithFindings(
          { remedyId },
          { object_ids: objects, ...rest },
          undefined,
          signal,
        );
        computedObjectIds.push(...(response?.object_ids ?? []));
      }

      return { object_ids: computedObjectIds };
    },
    ...options,
  });
};

export function useObjectsWithFindingsQuery(
  { remedyId }: GetObjectsWithFindingsPathParameters,
  request: TLObjectIdsRequest,
  options?: QueryOptions<TLObjectIdsResponse>,
) {
  const { maxFindingsObjectIdsPerRequest } = useCodeRemedyFlags();

  return useQuery(
    getObjectsWithFindingsQueryOptions(
      { remedyId },
      { ...request, max_findings_object_ids_per_request: maxFindingsObjectIdsPerRequest },
      options,
    ),
  );
}

export function useUpdateFindingStatusMutation(
  options?: MutationOptionsNext<
    TLSuccessResponse,
    TLAIApiError,
    TLUpdateFindingsStatusRequest & UpdateFindingsStatusPathParameters
  >,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({
      objectId,
      scanId,
      ...input
    }: TLUpdateFindingsStatusRequest & UpdateFindingsStatusPathParameters) =>
      updateFindingsStatus({ objectId, scanId }, input),
    invalidateQueries: (_, { objectId, scanId }) => [
      findingsV3QueryOptions({ object_id: objectId, scan_id: scanId }).queryKey,
    ],
  });
}

export const checkActiveCodeQueryOptions = (
  { remedyId }: CheckActiveCodePathParameters,
  input: TLValidateCodeRequest,
  options?: QueryOptions<TLValidateCodeResponse>,
) =>
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  queryOptions({
    queryKey: ['check-active-code', remedyId, input.object_id],
    queryFn: async ({ signal }) => checkActiveCode({ remedyId }, input, undefined, signal),
    ...options,
  });

export function useCheckActiveCodeQuery(
  { remedyId }: CheckActiveCodePathParameters,
  input: TLValidateCodeRequest,
  options?: QueryOptions<TLValidateCodeResponse>,
) {
  return useQuery(checkActiveCodeQueryOptions({ remedyId }, input, options));
}

export function useFormatAbapCodeMutation(
  options?: MutationOptionsNext<TLAbapFormatResponse, TLAIApiError, TLAbapFormatRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLAbapFormatRequest) => formatAbapCode(input),
  });
}

export function useApplyPseudocodeMutation(
  options?: MutationOptionsNext<TLApplyPseudocodeResponse, TLAIApiError, TLApplyPseudocodeRequest>,
) {
  return useMutationNext({
    ...options,
    mutationFn: async (input: TLApplyPseudocodeRequest) => applyPseudocode(input),
  });
}

export function useRemedyV3BulkTransportRequestsMutation(
  options?: MutationOptionsNext<
    TLRemedyTrBulkAcceptedResponse,
    TLAIApiError,
    { remedy_id: string } & TLRemedyTrBulkFromProgramRequest
  >,
) {
  return useMutationNext({
    ...options,
    mutationFn: async ({ remedy_id, program_id }: { remedy_id: string } & TLRemedyTrBulkFromProgramRequest) =>
      reconcileProgramFileSubobjectInsertParentTrViaAdtAsync({ remedyId: remedy_id }, { program_id }),
    invalidateQueries: (_, { remedy_id }) => [
      remedyV3DetailQueryOptions({ remedy_id }).queryKey,
      remediesV3QueryOptions().queryKey,
    ],
  });
}
